import { useEffect, useRef, useState } from 'react';

/**
 * Two-step destructive action, extracted from the orders table so every
 * delete in the app behaves identically: first click arms, second commits,
 * and it disarms itself after four seconds.
 *
 * This replaces `confirm()`, which the monolith avoided for a practical
 * reason worth keeping: native dialogs are blocked or auto-dismissed in
 * several of the embedded and automated browser contexts this gets demoed
 * in, so a "confirm" that silently returns false makes delete look broken.
 *
 * `hint` is the reassurance shown while armed — "every historical order for
 * this quarry stays as it is". It exists because these deletes are archives,
 * not deletions, and a dispatcher who believes the button destroys invoices
 * hesitates over a button that is actually safe. The label itself cannot say
 * so: it is a 40px button in a row of them.
 */
export function ConfirmButton({
  label,
  confirmLabel,
  hint,
  onConfirm,
  className = '',
  disabled = false,
}) {
  const [armed, setArmed] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const handleClick = () => {
    if (!armed) {
      setArmed(true);
      // A hint is a sentence, not a word. Four seconds is enough to notice
      // that a label changed and no more — not enough to read a line of
      // reassurance and decide on it.
      timer.current = window.setTimeout(() => setArmed(false), hint ? 8000 : 4000);
      return;
    }
    window.clearTimeout(timer.current);
    setArmed(false);
    onConfirm();
  };

  const button = (
    <button
      type="button"
      className={`btn btn-danger !px-2.5 !py-1 !text-[11px] ${className}`}
      onClick={handleClick}
      disabled={disabled}
    >
      {armed ? confirmLabel : label}
    </button>
  );

  if (!hint) return button;

  // Floated above the button rather than placed beside it: every caller sits
  // in a tight flex row of actions, and a sentence rendered inline would
  // reflow that row the moment the button arms — the layout jumping under
  // the cursor mid-click is how the wrong button gets pressed.
  return (
    <span className="relative inline-flex">
      {button}
      {armed ? (
        <span
          role="status"
          className="pointer-events-none absolute bottom-full right-0 z-20 mb-1.5 w-60 rounded-[9px] border border-line bg-panel-2 px-2.5 py-1.5 text-left text-[10.5px] font-normal normal-case leading-snug text-dim shadow-lg"
        >
          {hint}
        </span>
      ) : null}
    </span>
  );
}
